import styles from './Dashboard.module.css';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';

import { setEnvironment, getEnvironment } from '../../utils/apiConfig.js';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from "../../AuthContext";
import StatusOverview from './StatusOverview/StatusOverview';
import ControlPanel from './ControlPanel/ControlPanel';
import MessageQueueTable from './MessageQueueTable/MessageQueueTable';
import MonitoredShiftsTable from './MonitoredShiftsTable/MonitoredShiftsTable';
import NotificationPreferencesTable from './NotificationPreferencesTable/NotificationPreferencesTable';
import MessageDetailsModal from './MessageDetailsModal/MessageDetailsModal';

export const Dashboard = () => {
    const [ error, setError ] = useState(null);
    const [ user, setUser ] = useState(null);

    // UI state
    const [ autoRefresh, setAutoRefresh ] = useState(true);
    const [ alert, setAlert ] = useState({ open: false, message: '', severity: 'info' });
    const [ selectedMessage, setSelectedMessage ] = useState(null);
    const [ detailsModalOpen, setDetailsModalOpen ] = useState(false);
    const [ environment, setEnvironmentState ] = useState(getEnvironment());

    const { auth, claims, token } = useAuth();

    const queryClient = useQueryClient();

    const showAlert = (message, severity = 'info') => {
        setAlert({ open: true, message, severity });
    };

    const closeAlert = () => {
        setAlert(prev => ({ ...prev, open: false }));
    };

    // Administrative actions delegate to API and then trigger children refresh via react-query invalidation
    const handleProcessQueue = async () => {
        try {
            const result = await (await import('../../utils/dashboardAPI.js')).dashboardAPI.processQueue(token);
            
            showAlert(result.message || 'Queue processed successfully', 'success');

            // Invalidate all relevant caches so children refetch immediately
            queryClient.invalidateQueries(['messages']);
            queryClient.invalidateQueries(['preferences']);
            queryClient.invalidateQueries(['shifts']);
            queryClient.invalidateQueries(['queueStatus']);
            queryClient.invalidateQueries(['systemStatus']);
            // no refreshCounter fallback needed once all components use react-query
        } catch (error) {
            showAlert(`Failed to process queue: ${ error.message }`, 'error');
        }
    };

    const handleArchiveMessages = async () => {
        try {
            const result = await (await import('../../utils/dashboardAPI.js')).dashboardAPI.archiveMessages(token);

            showAlert(result.message || 'Messages archived successfully', 'success');

            queryClient.invalidateQueries(['messages']);
            queryClient.invalidateQueries(['preferences']);
            queryClient.invalidateQueries(['shifts']);
            queryClient.invalidateQueries(['queueStatus']);
            queryClient.invalidateQueries(['systemStatus']);
        } catch (error) {
            showAlert(`Failed to archive messages: ${ error.message }`, 'error');
        }
    };

    const handleClearFailed = async () => {
        try {
            const result = await (await import('../../utils/dashboardAPI.js')).dashboardAPI.clearFailedMessages(token);

            showAlert(result.message || 'Failed messages cleared successfully', 'success');

            queryClient.invalidateQueries(['messages']);
            queryClient.invalidateQueries(['preferences']);
            queryClient.invalidateQueries(['shifts']);
            queryClient.invalidateQueries(['queueStatus']);
            queryClient.invalidateQueries(['systemStatus']);
        } catch (error) {
            showAlert(`Failed to clear failed messages: ${ error.message }`, 'error');
        }
    };

    const handleRetryMessage = async (messageId) => {
        try {
            const result = await (await import('../../utils/dashboardAPI.js')).dashboardAPI.retryMessage(messageId, token);

            showAlert(result.message || 'Message queued for retry', 'success');

            queryClient.invalidateQueries(['messages']);
            queryClient.invalidateQueries(['preferences']);
            queryClient.invalidateQueries(['shifts']);
            queryClient.invalidateQueries(['queueStatus']);
            queryClient.invalidateQueries(['systemStatus']);
        } catch (error) {
            showAlert(`Failed to retry message: ${ error.message }`, 'error');
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (!window.confirm('Are you sure you want to delete this message?')) {
            return;
        }

        try {
            const result = await (await import('../../utils/dashboardAPI.js')).dashboardAPI.deleteMessage(messageId, token);

            showAlert(result.message || 'Message deleted successfully', 'success');

            queryClient.invalidateQueries(['messages']);
            queryClient.invalidateQueries(['preferences']);
            queryClient.invalidateQueries(['shifts']);
            queryClient.invalidateQueries(['queueStatus']);
            queryClient.invalidateQueries(['systemStatus']);
        } catch (error) {
            showAlert(`Failed to delete message: ${ error.message }`, 'error');
        }
    };

    const handleShowMessageDetails = (message) => {
        setSelectedMessage(message);
        setDetailsModalOpen(true);
    };

    const closeDetailsModal = () => {
        setDetailsModalOpen(false);
        setSelectedMessage(null);
    };

    const handleEnvironmentToggle = (newEnvironment) => {
        const currentEnv = getEnvironment();
        setEnvironment(newEnvironment);
        setEnvironmentState(newEnvironment);

        // If switching between emulator and live Firebase, warn about potential auth issues
        if (currentEnv === 'dev' || newEnvironment === 'dev') {
            showAlert(
                `Switched to ${ newEnvironment } environment. Firebase auth context changed - you may need to refresh and log in again.`,
                'warning'
            );

            // Optionally trigger a page reload for clean auth state
            setTimeout(() => {
                if (window.confirm('Refresh page to ensure clean authentication state?')) {
                    window.location.reload();
                }
            }, 2000);
                } else {
                    showAlert(`Switched to ${ newEnvironment } environment. Refreshing data...`, 'info');
                    // invalidate to refresh data after environment change
                    queryClient.invalidateQueries(['messages']);
                    queryClient.invalidateQueries(['preferences']);
                    queryClient.invalidateQueries(['shifts']);
                    queryClient.invalidateQueries(['queueStatus']);
                    queryClient.invalidateQueries(['systemStatus']);
                }
    };

    // auto-refresh is handled per-query via `refetchInterval` (controlled by autoRefresh prop)

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (u) => {
            setUser(u);
            if (!u) {
                setError('You must be logged in to access the dashboard.');

                return;
            }
        });

        return () => unsubscribe();
    }, []);

    // trigger an initial refresh once user is available
    useEffect(() => {
        if (user) {
            // initial data load via invalidation so queries refetch
            queryClient.invalidateQueries(['messages']);
            queryClient.invalidateQueries(['preferences']);
            queryClient.invalidateQueries(['shifts']);
            queryClient.invalidateQueries(['queueStatus']);
            queryClient.invalidateQueries(['systemStatus']);
        }
    }, [ user ]);

    // Verify backend session/token periodically and sign out immediately on 401/403.
    // Use AbortController so in-flight requests are cancelled when unmounting or before starting a new check.
    useEffect(() => {
        if (!user) return;

        let controller = null;
        let stopped = false;

        const checkServerAuth = async () => {
            try {
                // Abort any previous outstanding request before starting a new one
                if (controller) {
                    try { controller.abort(); } catch {/* ignore */ }
                }

                controller = new AbortController();
                const signal = controller.signal;

                const resp = await fetch(`${ process.env.REACT_APP_API_ENDPOINT }/api/v1/notify/dashboard/status`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${ token }`,
                        'Content-Type': 'application/json'
                    },
                    signal
                });

                if (stopped) return;

                if (resp.status === 401 || resp.status === 403) {
                    // inform user and sign out to stop further polling from child queries
                    setError('Session expired or unauthorized. Signing out.');

                    await signOutUser();
                }
            } catch (err) {
                // Ignore AbortError which indicates the request was intentionally cancelled
                if (err && err.name === 'AbortError') return;

                // network or other transient errors are ignored here; child queries will surface them
                console.debug('dashboard auth check failed', err);
            }
        };

        // initial check immediately
        checkServerAuth();

        // periodic check to detect token expiry while dashboard is open
        const intervalId = setInterval(checkServerAuth, 10 * 1000);

        return () => {
            stopped = true;

            clearInterval(intervalId);

            if (controller) {
                try { controller.abort(); } catch {/* ignore */ }
            }
        };
    }, [ user ]);

    if (!user) {
        return (
            <div>
                <Container maxWidth="xl" sx={ { mt: 3 } }>
                    { error && <Alert severity="warning">{ error }</Alert> }
                </Container>
            </div>
        );
    }

    return (
        <div className={ styles.dashboard }>
            { claims?.admin ? <Container maxWidth="100%" sx={ { mt: 3, mb: 3 } }>

                <StatusOverview autoRefresh={ autoRefresh } />

                <ControlPanel
                    onProcessQueue={ handleProcessQueue }
                    onArchiveMessages={ handleArchiveMessages }
                    onClearFailed={ handleClearFailed }
                    autoRefresh={ autoRefresh }
                    environment={ environment }
                    onAutoRefreshToggle={ setAutoRefresh }
                    onEnvironmentToggle={ handleEnvironmentToggle }
                />

                <MessageQueueTable
                    onShowDetails={ handleShowMessageDetails }
                    onRetry={ handleRetryMessage }
                    onDelete={ handleDeleteMessage }
                    autoRefresh={ autoRefresh }
                />

                <MonitoredShiftsTable autoRefresh={ autoRefresh } />

                <NotificationPreferencesTable autoRefresh={ autoRefresh } />

                <MessageDetailsModal
                    message={ selectedMessage }
                    open={ detailsModalOpen }
                    onClose={ closeDetailsModal }
                />

                <Snackbar
                    open={ alert.open }
                    autoHideDuration={ 6000 }
                    onClose={ closeAlert }
                    anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
                >
                    <Alert severity={ alert.severity } onClose={ closeAlert }>
                        { alert.message }
                    </Alert>
                </Snackbar>
            </Container> : <Container maxWidth="xl" sx={ { mt: 3 } }>
                <Alert severity="error">You do not have permission to access this dashboard.</Alert>
            </Container> }
        </div>
    );
};